import React from 'react';
import PropTypes from 'prop-types';
import { EM_DASH, formatNumber, formatEtDateTime } from './queueMonitorFormat';

/**
 * FailureAnomalyChart (v3 contract 5.1 + 5.3)
 *
 * Per-hour failure bars with anomaly highlighting, fed by
 * GET /api/violerts-queue/anomalies:
 *   {
 *     window_hours: 168, basis: "job_metrics_hourly",
 *     anomaly_multiplier: 4.0, min_baseline_hours: 48,
 *     points: [ { ts, failures, baseline_median, anomaly: true|false|null } ]
 *   }
 *
 * A dependency-free SVG bar chart (no chart library; the v3 contract permits the
 * same SVG primitive for a denser bar view). Each hour is one bar:
 * - anomaly === true  -> alert color, tooltip "{failures} vs ~{baseline_median} median (>{mult}x)".
 * - anomaly === false -> neutral color.
 * - anomaly === null  -> muted color, tooltip "baseline warming up" (below the
 *   min_baseline_hours coverage floor; never flagged, never silently normal).
 *
 * Locked empty/honesty states (v3 contract 5.3):
 * - data null (loading / pre-v3 backend) -> renders nothing.
 * - points empty -> honest "no failure data in window" message.
 *
 * Bars use native SVG <title> for hover tooltips (no JS handlers, no deps).
 */

const ANOMALY_COLOR = '#dc3545'; // alert red
const NORMAL_COLOR = '#0d6efd'; // neutral blue
const WARMING_COLOR = '#adb5bd'; // muted grey (baseline warming)

const CHART_HEIGHT = 80;
const BAR_GAP = 1;
const PADDING = 2;

const barColor = (anomaly) => {
	if (anomaly === true) {
		return ANOMALY_COLOR;
	}
	if (anomaly === null || anomaly === undefined) {
		return WARMING_COLOR;
	}
	return NORMAL_COLOR;
};

const barTitle = (point, multiplier) => {
	const failures = formatNumber(point.failures);
	if (point.anomaly === true) {
		const baseline =
			point.baseline_median === null || point.baseline_median === undefined
				? EM_DASH
				: formatNumber(point.baseline_median);
		const ts = point.ts ? `${formatEtDateTime(point.ts)} ET` : '';
		return `${ts}: ${failures} failures vs ~${baseline} median (>${multiplier}x)`;
	}
	if (point.anomaly === null || point.anomaly === undefined) {
		const ts = point.ts ? `${formatEtDateTime(point.ts)} ET` : '';
		return `${ts}: ${failures} failures (baseline warming up)`;
	}
	const baseline =
		point.baseline_median === null || point.baseline_median === undefined
			? EM_DASH
			: formatNumber(point.baseline_median);
	const ts = point.ts ? `${formatEtDateTime(point.ts)} ET` : '';
	return `${ts}: ${failures} failures (~${baseline} median)`;
};

const FailureAnomalyChart = ({ data }) => {
	if (!data) {
		// Loading / pre-v3 backend: render nothing.
		return null;
	}

	const points = Array.isArray(data.points) ? data.points : [];
	const multiplier =
		typeof data.anomaly_multiplier === 'number' ? data.anomaly_multiplier : 4;

	const anomalyCount = points.filter((p) => p && p.anomaly === true).length;

	if (points.length === 0) {
		return (
			<div className="analytics-card failure-anomaly-chart">
				<h5 className="mb-1">Failures by hour (anomaly-flagged)</h5>
				<p className="text-muted anomaly-empty mb-1">
					No failure data in window
				</p>
			</div>
		);
	}

	const failureValues = points
		.map((p) => (p && typeof p.failures === 'number' ? p.failures : 0))
		.filter((v) => Number.isFinite(v));
	const maxFailures = failureValues.length > 0 ? Math.max(...failureValues) : 0;

	// Fixed viewBox; bars stretch to width via preserveAspectRatio none.
	const chartWidth = Math.max(points.length, 1) * 4; // virtual px; scaled by CSS
	const innerHeight = CHART_HEIGHT - PADDING * 2;
	const barWidth = Math.max(
		1,
		(chartWidth - BAR_GAP * (points.length - 1)) / points.length,
	);

	const barHeight = (failures) => {
		if (maxFailures <= 0) {
			return 0;
		}
		const v = typeof failures === 'number' && failures > 0 ? failures : 0;
		return (v / maxFailures) * innerHeight;
	};

	return (
		<div className="analytics-card failure-anomaly-chart">
			<div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
				<h5 className="mb-0 me-2">Failures by hour (anomaly-flagged)</h5>
				<span className="anomaly-count text-muted small">
					{anomalyCount > 0
						? `${formatNumber(anomalyCount)} anomalous hour${anomalyCount === 1 ? '' : 's'} (> ${multiplier}x median)`
						: 'no anomalies in window'}
				</span>
			</div>
			<svg
				className="anomaly-bars"
				width="100%"
				height={CHART_HEIGHT}
				viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
				preserveAspectRatio="none"
				role="img"
				aria-label="failures per hour with anomaly highlighting"
			>
				{points.map((point, index) => {
					const h = barHeight(point ? point.failures : 0);
					const x = index * (barWidth + BAR_GAP);
					const y = CHART_HEIGHT - PADDING - h;
					return (
						<rect
							key={point && point.ts ? point.ts : `bar-${index}`}
							className="anomaly-bar"
							data-anomaly={
								point && point.anomaly === true
									? 'true'
									: point && (point.anomaly === null || point.anomaly === undefined)
										? 'null'
										: 'false'
							}
							x={x}
							y={y}
							width={barWidth}
							height={Math.max(0, h)}
							fill={barColor(point ? point.anomaly : false)}
						>
							<title>{barTitle(point || {}, multiplier)}</title>
						</rect>
					);
				})}
			</svg>
			<div className="anomaly-legend text-muted small mt-1 d-flex flex-wrap">
				<span className="me-3">
					<span
						className="anomaly-swatch"
						style={{
							display: 'inline-block',
							width: 10,
							height: 10,
							background: ANOMALY_COLOR,
							marginRight: 4,
						}}
					/>
					anomaly (&gt;{multiplier}x median)
				</span>
				<span className="me-3">
					<span
						className="anomaly-swatch"
						style={{
							display: 'inline-block',
							width: 10,
							height: 10,
							background: NORMAL_COLOR,
							marginRight: 4,
						}}
					/>
					normal
				</span>
				<span className="me-3">
					<span
						className="anomaly-swatch"
						style={{
							display: 'inline-block',
							width: 10,
							height: 10,
							background: WARMING_COLOR,
							marginRight: 4,
						}}
					/>
					baseline warming
				</span>
			</div>
			<div className="text-muted small mt-1">
				hourly failure totals; an hour is flagged when its failures exceed{' '}
				{multiplier}x the trailing-7-day median (basis{' '}
				{data.basis || 'job_metrics_hourly'})
			</div>
		</div>
	);
};

FailureAnomalyChart.propTypes = {
	// /anomalies payload; null = loading/pre-v3 backend (renders nothing).
	data: PropTypes.object,
};

FailureAnomalyChart.defaultProps = {
	data: null,
};

export default FailureAnomalyChart;
